import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Text,
  LegacyCard as Card,
  Checkbox,
  Select,
  InlineStack,
  BlockStack,
  TextField,
} from "@shopify/polaris";
import { useQuery, useMutation } from "@apollo/client";

// import Helper
import { baseHelper, errorHelper } from "lib/helpers";

// import constant
import constant from "lib/constant/constant";

// import component
import { Banner, Sheet, SkeletonCard, Toast } from "lib/components";

// import mutations
import { ADD_PRODUCT_FULFILLMENT } from "app/product/apollo/mutations";

//  import queries
import { GET_PRODUCT } from "app/product/apollo/queries";
import { GET_PRODUCT_FORM } from "app/setup/apollo/queries";

// import prop Type
import { tabProp } from "app/product/modules/generic/propTypes";

// import context
import { PrivateContext } from "lib/context/privateContext";
import { ProductContext } from "app/product/modules/generic/context";
import { useNavigate, useParams } from "react-router-dom";

const {
  SELECT_WEIGHT = "",
  value: { MIN_PRICE = 0, MAX_MEASUREMENT = 999 },
} = constant;

const Fulfillment = (props) => {
  const { tabObj = {} } = props;
  const { islastTab = false } = tabObj || {};
  const { handleTabChange, setBanner, setDescription, setTitle } = useContext(ProductContext);
  const [value, setValue] = useState({});
  const { cms, currentUser } = useContext(PrivateContext);
  const navigate = useNavigate();
  const { isAttachmentVisible = false } = currentUser || {};
  const { CRITICAL, gql, weightValue } = constant;
  const { id } = useParams();
  const [loader, setLoader] = useState(false);
  const [message, setMessage] = useState("");
  const [sheetActive, setSheetActive] = useState(false);
  const [disabledButton, setDisabledButton] = useState(true);
  const [physicalData, setPhysicalData] = useState("");
  // const [sheetTitle, setSheetTitle] = useState("");
  // const [sheetContent, setSheetContent] = useState("");
  const { OPTIONS } = constant;
  setDescription(cms("label.editFulfillment"));
  setTitle(cms("label.title.fulfillment"));
  const fieldData = [constant.IS_SHIPPING];

  const handleChange = (fieldName, fieldValue) => {
    const nextValue = fieldData.includes(fieldName) ? fieldValue : 0;

    setValue((prev) => ({
      ...prev,
      [fieldName]: nextValue,
    }));
  };

  const acceptOnlyValidInput = (val, measurementValue) => {
    return (baseHelper.validatePositiveNumericValues(val) && val) || (val !== "" && measurementValue);
  };

  const { loading: productLoading, data: productData } = useQuery(GET_PRODUCT, {
    variables: { input: { id } },
  });

  const { loading: loadingProductForm, data: dataProductForm } = useQuery(GET_PRODUCT_FORM);

  useEffect(() => {
    if (dataProductForm) {
      const productLabel = baseHelper.getResponseData(dataProductForm, gql.GET_PRODUCT_FORM_SETTING);
      if (productLabel && productLabel.title) {
        setPhysicalData(productLabel.physical);
      }
    }
  }, [dataProductForm, gql.GET_PRODUCT_FORM_SETTING]);

  useEffect(() => {
    if (productData) {
      const productResponse = baseHelper.getResponseData(productData, gql.GET_PRODUCT) || {};
      setValue(productResponse);
    }
  }, [gql.GET_PRODUCT, productData]);

  const [addProductFulfillment, { loading: addFulfillmentLoading }] = useMutation(ADD_PRODUCT_FULFILLMENT);

  const productListError = baseHelper.getResponseError(productData, gql.GET_PRODUCT);

  useEffect(() => {
    if (productListError) {
      setBanner({ isOpen: true, status: CRITICAL, title: productListError });
    }
  }, [CRITICAL, cms, productListError, setBanner]);

  if (productLoading || loadingProductForm) {
    return <SkeletonCard />;
  }

  const isVendor = baseHelper.isVendor(currentUser);

  const primaryAction = {
    content: cms("button.done"),
    onAction: () => setSheetActive(false),
  };

  const secondaryAction = {
    content: cms("button.cancel"),
    onAction: () => setSheetActive(false),
  };

  // const learnMore = (productItem, title) => {
  //   setSheetActive(true);
  //   setSheetTitle(title);
  //   setSheetContent(cms("label.todo")`${productItem}`);
  // };

  let length = (value.measurement && value.measurement.length && value.measurement.length.value) || "";
  let height = (value.measurement && value.measurement.height && value.measurement.height.value) || "";
  let width = (value.measurement && value.measurement.width && value.measurement.width.value) || "";

  const onFormSubmit = async () => {
    let banner = {};
    length = parseFloat(length);
    width = parseFloat(width);
    height = parseFloat(height);

    if (length > MAX_MEASUREMENT || width > MAX_MEASUREMENT || height > MAX_MEASUREMENT) {
      banner = {
        action: null,
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("message.error.measurement"),
      };
      setBanner(banner);
      return;
    }

    // const isLargeVolumetricWeight = baseHelper.isLargeVolumetricWeight(length, width, height);
    // if (isLargeVolumetricWeight) {
    //   banner = {
    //     action: null,
    //     isOpen: true,
    //     status: constant.CRITICAL,
    //     title: cms("message.error.volume"),
    //   };
    //   setBanner(banner);
    //   return;
    // }

    const formValues = {
      productId: id,
      isShipping: value.isShipping,
      weight: parseFloat(value.weight),
      weightUnit: value.weightUnit || weightValue.LB,
      measurement: {
        height: {
          value: height,
          unit: constant.CM,
        },
        length: {
          value: length,
          unit: constant.CM,
        },
        width: {
          value: width,
          unit: constant.CM,
        },
      },
    };

    const lengthValue = parseFloat(length);
    const widthValue = parseFloat(width);
    const heightValue = parseFloat(height);

    if (lengthValue > MAX_MEASUREMENT || widthValue > MAX_MEASUREMENT || heightValue > MAX_MEASUREMENT) {
      const bannerData = {
        action: null,
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("message.error.measurement"),
      };
      setBanner(bannerData);
      return;
    }

    if (isVendor && physicalData && physicalData.isRequired && !value.isShipping) {
      setBanner({
        action: null,
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("message.error.checkbox", {
          item: `${baseHelper.ucFirst(physicalData.label) || cms("label.physical")}`,
        }),
      });
      return;
    }

    await addProductFulfillment({ variables: { input: formValues } })
      .then((res) => {
        const responseError = baseHelper.getResponseError(res.data, gql.ADD_PRODUCT_FULFILLMENT);
        const responseData = baseHelper.getResponseData(res.data, gql.ADD_PRODUCT_FULFILLMENT);
        if (responseError) {
          setBanner({ isOpen: true, status: CRITICAL, title: responseError });
        }
        if (responseData) {
          setLoader(false);
          setMessage(cms("message.success.fulfillment"));
          setBanner({ isOpen: false, status: "", title: "" });
          setTimeout(() => {
            if (islastTab) {
              navigate("/products");
              return;
            }
            handleTabChange(5);
          }, 1500);
        }
      })
      .catch((exception) => {
        setBanner({ isOpen: true, status: CRITICAL, title: errorHelper.parse(exception) });
      });
  };
  const handleSkip = () => {
    handleTabChange(5);
  };
  const selectWeight = (
    <Select
      id="weightUnit"
      value={value.weightUnit || weightValue.LB}
      label={cms("label.unit")}
      onChange={(val) => {
        handleChange(SELECT_WEIGHT, val);
        setDisabledButton(false);
      }}
      placeholder={cms("placeholder.unit")}
      labelHidden
      options={OPTIONS}
    />
  );

  const handleMeasurement = (key, val) => {
    const { measurement = {} } = value || {};
    const data = (measurement && measurement[key]) || 0;
    if (measurement && measurement[key]) {
      delete measurement[key];
    }
    const { unit = "" } = data || {};
    const measurementData = {
      ...measurement,
      [key]: {
        value: val,
        unit,
      },
    };
    setValue({
      ...value,
      measurement: measurementData,
    });
  };

  const isNew = value && value.status && value.status.toLowerCase() === constant.NEW;

  const renderMeasurement = () => {
    return (
      <>
        <TextField
          id="length"
          label={cms("label.length")}
          min={MIN_PRICE}
          max={MAX_MEASUREMENT}
          value={length.toString() || ""}
          placeholder={cms("placeholder.length")}
          onChange={(val) => {
            handleMeasurement(constant.LENGTH, acceptOnlyValidInput(val, length));
            setDisabledButton(false);
          }}
        />
        <br />
        <TextField
          id="width"
          label={cms("label.width")}
          min={MIN_PRICE}
          max={MAX_MEASUREMENT}
          value={width.toString() || ""}
          placeholder={cms("placeholder.width")}
          onChange={(val) => {
            handleMeasurement(constant.WIDTH, acceptOnlyValidInput(val, width));
            setDisabledButton(false);
          }}
        />
        <br />
        <TextField
          id="height"
          label={cms("label.height")}
          min={MIN_PRICE}
          max={MAX_MEASUREMENT}
          value={height.toString() || ""}
          placeholder={cms("placeholder.height")}
          onChange={(val) => {
            handleMeasurement(constant.HEIGHT, acceptOnlyValidInput(val, height));
            setDisabledButton(false);
          }}
        />
      </>
    );
  };

  const required = (data) => {
    if (data && data.isRequired) {
      return "*";
    }
    return "";
  };

  const getLabel = (data, defaultCMS) => {
    if (data && data.label) {
      return data.label;
    }
    return defaultCMS;
  };

  return (
    <>
      <Card
        title={[
          cms("label.shipping"),
          value && value.title && (
            <Text as="span" tone="subdued">
              {value.updatedAt && (
                <Text variant="bodySm" as="p">{`${baseHelper.lastUpdateDate(value.updatedAt)}`}</Text>
              )}
            </Text>
          ),
        ]}
        id="productshipping"
        // actions={[
        //   {
        //     content: cms("label.more"),
        //     onAction: () => {
        //       learnMore("productshipping", cms("label.shipping"));
        //     },
        //   },
        // ]}
      >
        <Card.Section>
          <BlockStack>{cms("label.caption.weight")}</BlockStack>
          <br />
          <Checkbox
            id="shippingCheckbox"
            label={
              isVendor
                ? `${getLabel(physicalData, cms("label.physical"))}${required(physicalData)}`
                : cms("label.physical")
            }
            checked={value.isShipping || ""}
            onChange={(val) => {
              handleChange(constant.IS_SHIPPING, val);
              setDisabledButton(false);
            }}
          />
          {!value.isShipping && (
            <div>
              <br />
              <Banner isOpen status={constant.INFO} isScrollTop={false}>
                <p>{cms("label.uncheck")}</p>
              </Banner>
            </div>
          )}
        </Card.Section>
        {value.isShipping && (
          <Card.Section title={cms("label.weight")}>
            <p>{cms("label.caption.rate")}</p>
            <br />
            <TextField
              id="weight"
              label={cms("label.weight")}
              value={(value.weight && value.weight.toString()) || ""}
              placeholder={constant.ZERO_PRICE}
              onChange={(val) => {
                handleChange(constant.WEIGHT, acceptOnlyValidInput(val, value.weight));
                setDisabledButton(false);
              }}
              disabled={!value.weightUnit}
              connectedRight={selectWeight}
            />
            <br />
            {renderMeasurement()}
          </Card.Section>
        )}
      </Card>
      <div className="toast">
        <Toast message={message} />
      </div>
      <br />
      <InlineStack align="space-between">
        <Button onClick={() => handleTabChange(3)}>{cms("button.previous")}</Button>
        <InlineStack gap={400}>
          {((isNew && isAttachmentVisible) || !islastTab) && (
            <Button onClick={() => handleSkip()} disabled={addFulfillmentLoading}>
              {/* {isAttachmentVisible ? "Skip" : "Finish"} */}
              {cms("button.skip")}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => onFormSubmit()}
            disabled={islastTab ? false : disabledButton}
            loading={loader || addFulfillmentLoading}
          >
            {!isAttachmentVisible && islastTab ? cms("button.finish") : cms(`button.${islastTab ? "submit" : "save"}`)}
          </Button>
        </InlineStack>
      </InlineStack>
      {/* <PageActions
        primaryAction={{
          content: cms(`button.${islastTab ? "submit" : "save"}`),
          onAction: () => onFormSubmit(),
          loading: loader || addFulfillmentLoading,
          disabled: disabledButton,
        }}
        secondaryActions={[
          {
            content: cms("button.previous"),
            onAction: () => handleTabChange(3),
          },
        ]}
      /> */}
      {/* <Sheet
        // title={sheetTitle}
        isOpen={sheetActive}
        onClose={() => setSheetActive(false)}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      > */}
      {/* {sheetContent} */}
      {/* </Sheet> */}
    </>
  );
};

Fulfillment.propTypes = tabProp.type;

export default Fulfillment;
